import { useMemo } from 'react';
import { Grid, makeStyles, Theme, Toolbar, Typography } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';

import { ApiError, PortfolioQuery } from '../../../api';
import { SiteSummary } from '../../../models';
import { PortfolioFilter, PortfolioFilterOptions } from '../../../state/contexts/portfolioPage';

import { OrderDirectionType } from '../../../components/ui/DataTable';
import PortfolioTableContainer from './PortfolioTable';
import SiteFilter from './SiteFilter';

const PAGE_SIZE = 25;

interface ProfilePageProps {
  handleSearch: (searchText: string) => void;
  handleSort: (key: string, direction: OrderDirectionType) => void;
  handleChangePage: (page: number) => void;
  handleFilter: (filter: PortfolioFilter) => void;
  siteSummaries: SiteSummary[];
  activeFilters: PortfolioFilter;
  activePage: number;
  activeSort: OrderDirectionType;
  activeSearch?: string;
  sortBy: string;
  filterOptions?: PortfolioFilterOptions;
  totalSites?: number;
  loading: boolean;
  error?: ApiError;
  isAdmin?: boolean;
  query: PortfolioQuery;
  siteFetchTime?: Date;
}

const ProfilePage = (props: ProfilePageProps) => {
  const {
    handleFilter,
    handleSearch,
    handleChangePage,
    siteSummaries,
    activeFilters,
    activePage,
    activeSearch,
    filterOptions,
    totalSites,
    loading,
    error,
    isAdmin,
    query,
    siteFetchTime,
  } = props;

  const classes = useStyles({ loading });

  const showTable = totalSites !== undefined && siteSummaries.length > 0 && !loading && !error;

  const noFiltersSelected = useMemo(
    () => Object.values(activeFilters).every((filter) => !filter?.length),
    [activeFilters]
  );

  const showEquipmentList = useMemo(
    () => activeFilters.equipmentSerialNumber || activeFilters.equipmentFirmwareVersion,
    [activeFilters]
  );

  return (
    <Grid className={classes.root} container direction="column" spacing={1}>
      <Grid
        container
        item
        direction="row"
        style={{ flex: 0, justifyContent: 'center', padding: '24px 16px 0px' }}
      >
        <Grid container item xs={12} style={{ marginBottom: 8 }} spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h4" className={classes.h4}>
              {totalSites} Sites
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {filterOptions && (
              <SiteFilter
                loading={loading}
                isAdmin={isAdmin}
                handleFilterChange={handleFilter}
                currentFilters={activeFilters}
                filterOptions={filterOptions}
                handleSearch={handleSearch}
                activeSearch={activeSearch}
                query={query}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid className={classes.body} item>
        <PortfolioTableContainer
          pageElements={siteSummaries}
          noFiltersSelected={noFiltersSelected}
          loading={loading}
          error={error}
          showEquipmentList={!!showEquipmentList}
          siteFetchTime={siteFetchTime}
          query={query}
        />
      </Grid>

      {showTable && totalSites !== undefined && (
        <Grid item className={classes.footer}>
          <Toolbar>
            <Grid justifyContent="center" container alignItems="center" spacing={2}>
              <Grid item>
                <Pagination
                  count={Math.ceil(totalSites / PAGE_SIZE)}
                  page={activePage || 1}
                  variant="outlined"
                  shape="rounded"
                  onChange={(_e, newPage) => {
                    if (newPage === activePage) {
                      return;
                    }
                    handleChangePage(newPage);
                  }}
                />
              </Grid>
            </Grid>
          </Toolbar>
        </Grid>
      )}
    </Grid>
  );
};

interface StylesProps {
  loading: boolean;
}

const useStyles = makeStyles<Theme, StylesProps>((theme) => ({
  root: {
    ...theme.mixins.pageBackground,
    boxShadow: 'none',
    height: '100%',
  },
  footer: {
    borderTop: '1px solid #E8E8E8',
    backgroundColor: '#FFFFFF',
    padding: 0,
  },
  body: {
    position: 'relative',
    flex: 1,
    overflow: 'auto',
    width: '100%',
    padding: '0 4px !important',
  },
  chip: {
    margin: theme.spacing(0, 0.2),
  },
  h4: {
    fontSize: 22,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '0.25px',
  },
}));

export default ProfilePage;
